import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../../components/AppIcon';
import { workforceService } from '../../../../services/workforceService';
import { useCompany } from '../../../../contexts/CompanyContext';
import { useAuth } from '../../../../contexts/AuthContext';

const AddPersonnelModal = ({ isOpen, onClose, type, onSuccess }) => {
  const { currentCompany } = useCompany();
  useAuth(); // Ensure user is authenticated

  // Get the user's role from currentCompany (from CompanyContext)
  const userRole = currentCompany?.role;

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    username: '',
    role: type === 'contractor' ? 'contractor' : '',
    password: ''
  });

  const [usernameStatus, setUsernameStatus] = useState({
    checking: false,
    available: null,
    error: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);

  // Get available roles based on user's role
  useEffect(() => {
    if (userRole) {
      const roles = workforceService.getRolesUserCanCreate(userRole, type);
      setAvailableRoles(roles);
      // Set default role if available
      if (roles.length > 0 && !formData.role) {
        setFormData(prev => ({ ...prev, role: roles[roles.length - 1].value }));
      }
    }
  }, [userRole, type]);

  // Generate password on mount
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        password: workforceService.generatePassword()
      }));
    }
  }, [isOpen]);

  // Debounced username check
  useEffect(() => {
    if (!formData.username || formData.username.length < 3) {
      setUsernameStatus({ checking: false, available: null, error: null });
      return;
    }

    const timeoutId = setTimeout(async () => {
      setUsernameStatus({ checking: true, available: null, error: null });
      try {
        const result = await workforceService.checkUsernameAvailable(formData.username);
        setUsernameStatus({
          checking: false,
          available: result.available,
          error: result.error
        });
      } catch (err) {
        setUsernameStatus({
          checking: false,
          available: false,
          error: 'Failed to check username'
        });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.username]);

  const handleChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitError(null);
  };

  const handleUsernameChange = (e) => {
    // Only allow lowercase alphanumeric and underscore
    const value = e?.target?.value?.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setFormData(prev => ({ ...prev, username: value }));
    setSubmitError(null);
  };

  const handleGeneratePassword = () => {
    setFormData(prev => ({
      ...prev,
      password: workforceService.generatePassword()
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError(null);

    // Validate username is available
    if (!usernameStatus.available) {
      setSubmitError('Please choose an available username');
      return;
    }

    // Validate password
    const passwordCheck = workforceService.validatePassword(formData.password);
    if (!passwordCheck.valid) {
      setSubmitError(passwordCheck.error);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await workforceService.createWorkforceMember({
        companyId: currentCompany?.id,
        fullName: formData.fullName,
        email: formData.email,
        username: formData.username,
        role: formData.role,
        password: formData.password
      });

      if (!result.success) {
        setSubmitError(result.error || 'Failed to create member');
        setIsSubmitting(false);
        return;
      }

      // Success - call onSuccess with the result
      onSuccess?.(result);
      handleClose();
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      fullName: '',
      email: '',
      username: '',
      role: type === 'contractor' ? 'contractor' : '',
      password: ''
    });
    setUsernameStatus({ checking: false, available: null, error: null });
    setSubmitError(null);
    onClose();
  };

  if (!isOpen) return null;

  const canSubmit = formData.fullName &&
    formData.email &&
    formData.username &&
    formData.role &&
    formData.password &&
    usernameStatus.available &&
    !isSubmitting;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-xl w-full max-w-md mx-4 border border-border max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-500 bg-opacity-20 rounded-lg flex items-center justify-center">
              <Icon name="UserPlus" size={20} color="#f97316" />
            </div>
            <h3 className="text-xl font-semibold text-text-primary">
              Add {type === 'contractor' ? 'Contractor' : 'Employee'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-text-secondary text-sm font-medium mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="fullName"
              value={formData?.fullName}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-border border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-orange-500"
              placeholder="John Doe"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-text-secondary text-sm font-medium mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-border border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-orange-500"
              placeholder="john.doe@example.com"
            />
          </div>

          {/* Username */}
          <div>
            <label className="block text-text-secondary text-sm font-medium mb-2">
              Username *
            </label>
            <div className="relative">
              <input
                type="text"
                name="username"
                value={formData?.username}
                onChange={handleUsernameChange}
                required
                maxLength={30}
                className="w-full px-4 py-2 bg-border border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-orange-500 pr-24"
                placeholder="johndoe"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary text-sm">
                @mervo
              </span>
            </div>
            {/* Username status indicator */}
            <div className="mt-1 h-5">
              {usernameStatus.checking && (
                <span className="text-text-secondary text-xs flex items-center">
                  <Icon name="Loader2" size={12} className="animate-spin mr-1" />
                  Checking...
                </span>
              )}
              {!usernameStatus.checking && usernameStatus.available === true && (
                <span className="text-green-500 text-xs flex items-center">
                  <Icon name="Check" size={12} className="mr-1" />
                  {formData.username}@mervo is available
                </span>
              )}
              {!usernameStatus.checking && usernameStatus.available === false && (
                <span className="text-red-500 text-xs flex items-center">
                  <Icon name="X" size={12} className="mr-1" />
                  {usernameStatus.error || 'Username not available'}
                </span>
              )}
            </div>
          </div>

          {/* Role - only show for employees */}
          {type === 'employee' && (
            <div>
              <label className="block text-text-secondary text-sm font-medium mb-2">
                Role *
              </label>
              <select
                name="role"
                value={formData?.role}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-border border border-border rounded-lg text-text-primary focus:outline-none focus:border-orange-500"
              >
                <option value="" disabled>Select a role</option>
                {availableRoles?.map(role => (
                  <option key={role?.value} value={role?.value}>
                    {role?.label}
                  </option>
                ))}
              </select>
              {availableRoles.length === 0 && (
                <p className="text-red-500 text-xs mt-1">
                  You don't have permission to create any employee roles
                </p>
              )}
            </div>
          )}

          {/* Password */}
          <div>
            <label className="block text-text-secondary text-sm font-medium mb-2">
              Password *
            </label>
            <div className="relative">
              <input
                type="text"
                name="password"
                value={formData?.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-border border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-orange-500 pr-24 font-mono"
              />
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs bg-surface-light text-text-secondary rounded hover:bg-surface transition-colors"
              >
                Generate
              </button>
            </div>
            <p className="text-text-secondary text-xs mt-1">
              Min 8 characters with at least 1 letter and 1 number
            </p>
          </div>

          {/* Error message */}
          {submitError && (
            <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-3">
              <p className="text-red-400 text-sm flex items-center">
                <Icon name="AlertCircle" size={16} className="mr-2" />
                {submitError}
              </p>
            </div>
          )}

          {/* Info box */}
          <div className="bg-border rounded-lg p-4">
            <p className="text-text-secondary text-sm">
              <Icon name="Info" size={16} className="inline mr-2" />
              {type === 'contractor'
                ? 'The contractor will use these credentials to log in at /contractor-login'
                : 'The employee will use these credentials to log in at /corporate-login'}
            </p>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-6 py-2 border border-border rounded-lg text-text-secondary hover:text-text-primary hover:border-gray-500 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg text-white font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting && (
                <Icon name="Loader2" size={16} className="animate-spin mr-2" />
              )}
              {isSubmitting ? 'Creating...' : `Create ${type === 'contractor' ? 'Contractor' : 'Employee'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPersonnelModal;
